import { useState } from 'react';
import { BenchmarkReport, formatBenchmarkReport, shareBenchmark } from 'utils/benchmark';

/**
 * Centered modal that shows a finished benchmark report. The report text is in a
 * read-only textarea so it's easy to select/copy-paste; buttons copy, share
 * (native sheet → clipboard), or close. Rendered by PerfHud when a run finishes.
 */
type Props = {
  report: BenchmarkReport;
  onClose: () => void;
};

const BenchmarkReportModal = ({ report, onClose }: Props) => {
  const [copied, setCopied] = useState(false);
  const text = formatBenchmarkReport(report);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — the textarea is selectable as a fallback.
    }
  };

  return (
    <div
      // Full-screen backdrop; click outside the card closes.
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000000, // above the HUD (999999)
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16
      }}>
      <div
        // Stop propagation so clicks inside the card don't close it.
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#12100c',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 8,
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          width: 'min(560px, 100%)',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid rgba(255,255,255,0.12)'
          }}>
          <strong style={{ fontSize: 14 }}>Benchmark report — {report.label}</strong>
          <span
            role='button'
            aria-label='Close report'
            onClick={onClose}
            style={{
              cursor: 'pointer',
              color: '#f87171',
              fontSize: 18,
              lineHeight: 1,
              padding: 4
            }}>
            ✕
          </span>
        </div>

        {report.series ? (
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.12)'
            }}>
            <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
              x-axis = time (each point is a 250ms window)
            </div>
            <ChartRow
              label={`FPS   avg ${report.fps.avg} · min ${report.fps.min}`}
              hint='frames/sec per window · higher = smoother · dips = slowdowns · dashed = 30fps'
              values={report.series.fps}
              stroke='#4ade80'
              unit=''
              threshold={30}
              durationSec={report.durationSec}
            />
            <ChartRow
              label={`worst frame   peak ${report.worstFrameMs}ms`}
              hint='slowest single frame per window · a spike = one janky frame · dashed = 50ms budget'
              values={report.series.worstMs}
              stroke='#fbbf24'
              unit='ms'
              threshold={50}
              durationSec={report.durationSec}
            />
            {report.memory.supported && report.series.memMb.length ? (
              <ChartRow
                label={`heap   peak ${report.memory.peakMb}MB`}
                hint='JS heap over time · upward drift = growth · sudden drops = garbage collection'
                values={report.series.memMb}
                stroke='#60a5fa'
                unit='MB'
                zeroBaseline={false}
                durationSec={report.durationSec}
              />
            ) : null}
          </div>
        ) : null}

        <textarea
          readOnly
          value={text}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            margin: 0,
            padding: 12,
            border: 'none',
            resize: 'none',
            backgroundColor: '#0a0908',
            color: '#e5e7eb',
            fontFamily: 'monospace',
            fontSize: 12,
            lineHeight: 1.5,
            whiteSpace: 'pre',
            overflow: 'auto',
            minHeight: 160
          }}
        />

        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '10px 14px',
            borderTop: '1px solid rgba(255,255,255,0.12)'
          }}>
          <button type='button' onClick={copy} style={btnStyle('#60a5fa')}>
            {copied ? '✓ copied' : '📋 copy'}
          </button>
          <button
            type='button'
            onClick={() => void shareBenchmark(report)}
            style={btnStyle('#4ade80')}>
            ⇪ share
          </button>
          <button
            type='button'
            onClick={onClose}
            style={{ ...btnStyle('#9ca3af'), marginLeft: 'auto' }}>
            close
          </button>
        </div>
      </div>
    </div>
  );
};

// A labeled sparkline row with y-axis min/max, an x-axis time span, and a
// one-line caption explaining what a spike means. Pure inline SVG — no chart
// library, no runtime cost beyond rendering once when the modal opens.
const AXIS = '#6b7280';
const ChartRow = ({
  label,
  hint,
  values,
  stroke,
  unit = '',
  threshold,
  zeroBaseline = true,
  durationSec
}: {
  label: string;
  hint: string;
  values: number[];
  stroke: string;
  unit?: string;
  threshold?: number;
  zeroBaseline?: boolean;
  durationSec: number;
}) => {
  if (!values.length) {
    return (
      <div style={{ marginBottom: 10, fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' }}>
        {label} — no data
      </div>
    );
  }
  const max = Math.max(...values, threshold ?? -Infinity);
  const min = zeroBaseline ? 0 : Math.min(...values);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#e5e7eb', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            textAlign: 'right',
            minWidth: 36,
            fontFamily: 'monospace',
            fontSize: 9,
            color: AXIS
          }}>
          <span>
            {Math.round(max)}
            {unit}
          </span>
          <span>
            {Math.round(min)}
            {unit}
          </span>
        </div>
        <div style={{ flex: 1 }}>
          <Sparkline values={values} stroke={stroke} threshold={threshold} min={min} max={max} />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: 'monospace',
              fontSize: 9,
              color: AXIS,
              marginTop: 2
            }}>
            <span>0s</span>
            <span>{durationSec}s</span>
          </div>
        </div>
      </div>
      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{hint}</div>
    </div>
  );
};

const Sparkline = ({
  values,
  stroke,
  threshold,
  min,
  max,
  width = 500,
  height = 40
}: {
  values: number[];
  stroke: string;
  threshold?: number;
  min: number;
  max: number;
  width?: number;
  height?: number;
}) => {
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : width;
  const points = values
    .map(
      (v, i) => `${(i * stepX).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`
    )
    .join(' ');
  const thY = threshold !== undefined ? height - ((threshold - min) / range) * height : null;
  return (
    <svg
      width='100%'
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio='none'
      style={{ display: 'block', background: '#0a0908', borderRadius: 4 }}>
      {thY !== null ? (
        <line
          x1={0}
          y1={thY}
          x2={width}
          y2={thY}
          stroke='#ffffff'
          strokeOpacity={0.2}
          strokeDasharray='4 4'
          strokeWidth={1}
        />
      ) : null}
      <polyline
        points={points}
        fill='none'
        stroke={stroke}
        strokeWidth={1.5}
        vectorEffect='non-scaling-stroke'
      />
    </svg>
  );
};

const btnStyle = (color: string): React.CSSProperties => ({
  backgroundColor: 'transparent',
  color,
  border: `1px solid ${color}`,
  borderRadius: 6,
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer'
});

export default BenchmarkReportModal;
